// The Manufacturing Line List (plan) a user picks in a section's finder is mirrored in the address bar so
// the selection survives a page refresh and the link can be shared. Each section owns one query parameter,
// manufacturing_line_<index>, whose value serializes the plan's entries. A plan is a list of entries, each
// referencing a line in a group by { group_index, identifier } (the same entries the section posts back as
// manufacturing_line_list).
//
// The address bar carries just the machine ids, which are already inside the line's identifier:
// ManufacturingLine#identifier is "manufacturing_line:offset:<printId>:<dieId>" (an offset pair) or
// "manufacturing_line:flexo:<id>" (a combined flexo line). So the ids are the identifier's parts after
// "manufacturing_line:<type>:", and the id COUNT recovers the type (2 ids -> offset pair, 1 id -> flexo).
// An entry is stored as "<printId>-<dieId>" (or "<id>" for flexo), prefixed with "<group_index>." only when
// the group is not 0, and a plan's entries are joined with "_". Every character is URL-safe, so the address
// bar shows e.g. "manufacturing_line_0=10031-12008" instead of the percent-encoded identifier. Sections are
// keyed by the same `index` the finder already uses for its fetch, so the parameter is stable across reloads.

const parameterName = (index) => `manufacturing_line_${index}`;

const MACHINE_ID_SEPARATOR = '-';
const GROUP_SEPARATOR = '.';
const ENTRY_SEPARATOR = '_';

// The machine ids inside a line identifier: everything after "manufacturing_line:<type>:".
const machineIdsOf = (identifier) => identifier.split(':').slice(2);

// Serialize one plan entry to "<group_index>.<printId>-<dieId>" (the "<group_index>." prefix is dropped when
// the group is 0, the common single-part case, so it reads as just the machine ids).
const serializeEntry = (entry) => {
  const idPart = machineIdsOf(entry.identifier).join(MACHINE_ID_SEPARATOR);
  return entry.group_index === 0 ? idPart : `${entry.group_index}${GROUP_SEPARATOR}${idPart}`;
};

// Parse one token back to { group_index, identifier }, reconstructing the identifier from the machine id
// count (2 ids -> offset pair, 1 id -> flexo combined). The group index, when present, is the part before the
// first "." (machine ids never contain "."). Returns null for a malformed token (a non-numeric group index or
// machine id, or an id count other than 1 or 2) so a corrupt / stale-format param is rejected rather than
// posted as a bogus selection.
const parseEntry = (token) => {
  const groupSeparatorIndex = token.indexOf(GROUP_SEPARATOR);
  const hasGroup = groupSeparatorIndex >= 0;
  const groupIndex = hasGroup ? Number(token.slice(0, groupSeparatorIndex)) : 0;
  const idPart = hasGroup ? token.slice(groupSeparatorIndex + 1) : token;
  const ids = idPart.split(MACHINE_ID_SEPARATOR);

  if (Number.isNaN(groupIndex)) { return null; }
  if (!ids.every((id) => id !== '' && !Number.isNaN(Number(id)))) { return null; }

  if (ids.length === 2) {
    return { group_index: groupIndex, identifier: `manufacturing_line:offset:${ids[0]}:${ids[1]}` };
  }
  if (ids.length === 1) {
    return { group_index: groupIndex, identifier: `manufacturing_line:flexo:${ids[0]}` };
  }
  return null;
};

// A Manufacturing Line List (plan)'s stable key: its entries serialized (machine ids, group-prefixed),
// joined by "_". This SAME string is both the value stored in the URL and the key the finder uses to compare
// / select plans and to key its fetch, so the two can never drift. Null / empty list yields an empty string.
export const manufacturingLineListKey = (list) => (list || []).map(serializeEntry).join(ENTRY_SEPARATOR);

// The selected plan's entries for this section ([{ group_index, identifier }, ...]), or null when none is
// selected or the stored value is malformed. Each entry is exactly the shape the section posts back as
// manufacturing_line_list. If ANY entry fails to parse the whole param is treated as "no selection" (falls
// back to the listing) rather than restoring a partial / bogus plan.
export const readSelectedManufacturingLineList = (index) => {
  const value = new URLSearchParams(window.location.search).get(parameterName(index));
  if (!value) { return null; }
  const entries = value.split(ENTRY_SEPARATOR).map(parseEntry);
  if (entries.some((entry) => entry === null)) { return null; }
  return entries.length > 0 ? entries : null;
};

// Keep the address bar in sync with this section's selected plan so a refresh / shared link reproduces it.
// replaceState (not pushState) is used deliberately — this is a mirror, not in-page navigation: it adds
// no history entries, so the browser Back button never lands mid-flow (which would desync once a solution
// is picked and this finder unmounts). A same-origin relative URL is used so only the query string changes.
export const writeSelectedManufacturingLineList = (index, list) => {
  const url = new URL(window.location.href);
  url.searchParams.set(parameterName(index), manufacturingLineListKey(list));
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
};

// Remove this section's plan from the address bar (used by "back to machines").
export const clearSelectedManufacturingLineList = (index) => {
  const url = new URL(window.location.href);
  url.searchParams.delete(parameterName(index));
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
};
